import { styled } from '@nykaa/ui-components';
import { keyframes } from '@emotion/core';

const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const slideDown = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(100%);
  }
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

interface IOverlayProps {
  isClosing?: boolean;
}

export const Overlay = styled.div<IOverlayProps>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(1px);
  z-index: var(--overlay-z-index, 1000);
  animation: ${({ isClosing }) => (isClosing ? fadeOut : fadeIn)} 0.3s ease-out forwards;
`;

interface ISheetContainerProps {
  isClosing?: boolean;
}

export const SheetContainer = styled.div<ISheetContainerProps>`
  position: fixed;
  bottom: ${({ theme }) => theme.spacing.spacing80};
  transform: translateX(-50%);
  width: 100%;
  max-width: calc(100vw - ${({ theme }) => theme.spacing.spacing80});
  background-color: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borders.radius40};
  z-index: calc(var(--overlay-z-index, 1000) + 1);
  animation: ${({ isClosing }) => (isClosing ? slideDown : slideUp)} 0.3s ease-out forwards;
  margin-left: ${({ theme }) => theme.spacing.spacing40};
`;

export const ImageContainer = styled.div`
  width: 100%;
  height: 172px;
  border-radius: ${({ theme }) => `${theme.borders.radius40} ${theme.borders.radius40} 0 0`};
  overflow: hidden;
  position: relative;
`;

interface IBannerImageProps {
  isLoaded?: boolean;
}

export const BannerImage = styled.img<IBannerImageProps>`
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: ${({ isLoaded }) => (isLoaded ? 1 : 0)};
  transition: opacity 0.3s ease;
`;

export const AppLogo = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 135px;
  width: 74px;
  height: 74px;
  background-color: #001325;
  border: 2.643px solid ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borders.radius35};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  overflow: hidden;

  img, svg {
    width: 53px;
    height: auto;
  }
`;

export const ContentSection = styled.div`
  padding: 62px ${({ theme }) => theme.spacing.spacing120} ${({ theme }) => theme.spacing.spacing120};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

export const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.spacing40};
  margin-bottom: ${({ theme }) => theme.spacing.spacing120};
`;

export const Title = styled.h2`
  ${({ theme }) => theme.typography.type162};
  letter-spacing: 0.1px;
  color: rgba(0, 19, 37, 0.92);
  margin: 0;
  font-feature-settings: 'lnum' 1, 'pnum' 1;
`;

export const Subtitle = styled.p`
  ${({ theme }) => theme.typography.type120};
  color: rgba(0, 19, 37, 0.64);
  margin: 0;
  font-feature-settings: 'lnum' 1, 'pnum' 1;
`;

export const SubtitleRotator = styled.div`
  position: relative;
  height: 16px;
  overflow: hidden;
  width: 100%;
`;

interface ISubtitleTrackProps {
  activeIndex: number;
  disableTransition?: boolean;
}

export const SubtitleTrack = styled.div<ISubtitleTrackProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  transition: ${({ disableTransition }) => (disableTransition ? 'none' : 'transform 0.4s linear')};
  transform: translateY(${({ activeIndex }) => `-${activeIndex * 16}px`});
`;

export const SubtitleItem = styled.span`
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  flex-shrink: 0;
`;

export const Description = styled.p`
  ${({ theme }) => theme.typography.type120};
  color: rgba(0, 19, 37, 0.64);
  margin: 0;
  font-feature-settings: 'lnum' 1, 'pnum' 1;
`;

export const DownloadButton = styled.button`
  width: 100%;
  max-width: 296px;
  background-color: #001325;
  color: white;
  border: none;
  padding: ${({ theme }) => `${theme.spacing.spacing60} ${theme.spacing.spacing80}`};
  cursor: pointer;
  ${({ theme }) => theme.typography.type162};
  letter-spacing: 0.1px;
  text-align: center;
  font-feature-settings: 'lnum' 1, 'pnum' 1;
  border-radius: ${({ theme }) => theme.borders.radius10};

  &:active {
    opacity: 0.9;
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  left: 0;
  top: calc(-1 * ${({ theme }) => theme.spacing.spacing240});
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  svg {
    width: 24px;
    height: 24px;
  }
`;
